import React, { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import './LaporanKerusakan.css'; // Import the CSS file

// Helper to format Firestore Timestamp to readable date string
const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toLocaleDateString('id-ID'); // Indonesian locale
  }
  // If it's already a string (e.g., from input type="date"), return it
  if (typeof timestamp === 'string' && timestamp.match(/^\d{4}-\d{2}-\d{2}$/)) {
     return new Date(timestamp).toLocaleDateString('id-ID');
  }
  return 'Invalid Date';
};

// Helper to convert date string 'YYYY-MM-DD' to Firestore Timestamp or null
const dateStringToTimestamp = (dateString) => {
  if (!dateString) return null;
  try {
    return Timestamp.fromDate(new Date(dateString));
  } catch {
    return null; // Handle invalid date string
  }
};

// Helper to format date for input type="date" (YYYY-MM-DD)
const formatForInput = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toISOString().split('T')[0];
  }
  return ''; // Default for input if no date
};


const LaporanKerusakan = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingStatus, setEditingStatus] = useState({}); // { reportId: { status: '...', tanggal: '...', biaya: '...' } }

  // Fetch reports
  const fetchReports = async () => {
    setLoading(true);
    setError('');
    try {
      const reportsRef = collection(db, 'laporan_kerusakan');
      // Order by report date descending
      const q = query(reportsRef, orderBy('tanggalLaporan', 'desc'));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setReports(data);
    } catch (err) {
      console.error("Error fetching damage reports:", err);
      setError('Gagal memuat data laporan kerusakan.');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchReports();
  }, []);

  // Handle changes in the editing inputs/select
  const handleEditChange = (reportId, field, value) => {
    setEditingStatus(prev => ({
      ...prev,
      [reportId]: {
        ...prev[reportId],
        [field]: value,
      },
    }));
  };

  // Update report status and details in Firestore
  const handleUpdateStatus = async (reportId) => {
    const editedData = editingStatus[reportId];
    if (!editedData || !editedData.statusPerbaikan) {
      setError(`Pilih status perbaikan untuk laporan ${reportId}.`);
      return;
    }

    setLoading(true); // Consider using a specific loading state per row if needed
    setError('');
    try {
      const reportRef = doc(db, 'laporan_kerusakan', reportId);
      const updateData = {
        statusPerbaikan: editedData.statusPerbaikan,
        // Convert date string to Timestamp before saving
        tanggalPerbaikan: dateStringToTimestamp(editedData.tanggalPerbaikan),
        // Ensure biaya is stored as a number
        biayaPerbaikan: Number(editedData.biayaPerbaikan) || 0,
      };

      await updateDoc(reportRef, updateData);

      // Update local state
      setReports(prevReports =>
        prevReports.map(report =>
          report.id === reportId ? { ...report, ...updateData } : report
        )
      );

      // Clear editing state for this report
      setEditingStatus(prev => {
        const newState = { ...prev };
        delete newState[reportId];
        return newState;
      });

      console.log(`Damage report ${reportId} status updated.`);
    } catch (err) {
      console.error(`Error updating damage report ${reportId}:`, err);
      setError(`Gagal memperbarui status laporan ${reportId}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="laporan-kerusakan-container">
      <h2>Laporan Kerusakan dan Perbaikan</h2>

      {error && <div className="error-message">{error}</div>}

      {loading && !reports.length ? (
        <div className="loading-message">Memuat laporan...</div>
      ) : (
        <div className="laporan-kerusakan-table-container">
          <table className="laporan-kerusakan-table">
            <thead>
              <tr>
                <th>Nama Alat</th>
                <th>Jenis Alat</th>
                <th>Deskripsi Kerusakan</th>
                <th>Tgl Laporan</th>
                <th>Status Perbaikan</th>
                <th>Tgl Perbaikan</th>
                <th>Biaya (Rp)</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => {
                const isEditing = !!editingStatus[report.id];
                const currentEditData = editingStatus[report.id] || {};
                const currentStatus = isEditing ? currentEditData.statusPerbaikan : report.statusPerbaikan;
                const currentTanggal = isEditing ? currentEditData.tanggalPerbaikan : formatForInput(report.tanggalPerbaikan);
                const currentBiaya = isEditing ? currentEditData.biayaPerbaikan : report.biayaPerbaikan;

                return (
                  <tr key={report.id}>
                    <td>{report.namaAlat || 'N/A'}</td>
                    <td>{report.jenisAlat || 'N/A'}</td>
                    <td>{report.deskripsiKerusakan || 'N/A'}</td>
                    <td>{formatDate(report.tanggalLaporan)}</td>
                    <td>
                      {isEditing ? (
                        <select
                          value={currentStatus || ''}
                          onChange={(e) => handleEditChange(report.id, 'statusPerbaikan', e.target.value)}
                        >
                          <option value="" disabled>Pilih Status</option>
                          <option value="dilaporkan">Dilaporkan</option>
                          <option value="diperbaiki">Diperbaiki</option>
                          <option value="tidak_bisa_diperbaiki">Tidak Bisa Diperbaiki</option>
                        </select>
                      ) : (
                        <span className={`status-badge status-${report.statusPerbaikan || 'dilaporkan'}`}>
                          {report.statusPerbaikan || 'Dilaporkan'}
                        </span>
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          type="date"
                          value={currentTanggal || ''}
                          onChange={(e) => handleEditChange(report.id, 'tanggalPerbaikan', e.target.value)}
                        />
                      ) : (
                        formatDate(report.tanggalPerbaikan)
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          placeholder="Biaya"
                          value={currentBiaya || ''}
                          onChange={(e) => handleEditChange(report.id, 'biayaPerbaikan', e.target.value)}
                        />
                      ) : (
                        report.biayaPerbaikan ? report.biayaPerbaikan.toLocaleString('id-ID') : 'N/A'
                      )}
                    </td>
                    <td className="action-cell">
                      {isEditing ? (
                        <button
                          onClick={() => handleUpdateStatus(report.id)}
                          className="btn btn-update-status"
                          disabled={loading || !currentStatus}
                        >
                          Simpan
                        </button>
                      ) : (
                        <button
                          onClick={() => handleEditChange(report.id, 'statusPerbaikan', report.statusPerbaikan)} // Start editing
                          className="btn btn-update-status"
                          disabled={loading}
                        >
                          Update Status
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {reports.length === 0 && !loading && (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }}>Tidak ada laporan kerusakan.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LaporanKerusakan;
